import { MRT_ColumnDef, MaterialReactTable, useMaterialReactTable } from 'material-react-table'
import React, { useMemo } from 'react'
import { Funcoes } from '../../Utils/Funcoes'
import { MRT_Localization_PT_BR } from 'material-react-table/locales/pt-BR'
import { GradeProduto, ProdutoEstoque, VariacaoProduto, bgLineGridColorDark, bgLineGridColorLight, corBgGrid } from '../../Utils/Types'
import { useSelector } from 'react-redux'
import { selectUser } from '../Redux/userSlice'
import { ThemeProvider, createTheme } from '@mui/material'

type Props = {
    produtoEstoque: ProdutoEstoque[],
}

interface RowData {
    id: number,
    id_empresa: number,
    descricao: string,
    estoque_fisico: number,
    preco_c: number,
    preco_v: number,
    ativo: boolean,
    total_custo: number,
    total_venda: number,
    description: string,
    grades: [
        {
            id?: number,
            idProduto: number,
            codBarraRef: string,
            variacao: VariacaoProduto,
            subVariacao?: VariacaoProduto,
            saldoEstoque: number,
            valorEntrada?: number,

        }
    ],
}

export const GridProductsEstoque = React.memo(({ produtoEstoque }: Props) => {

    let user = useSelector(state => selectUser(state))


    //---------------------------------------------------------------------------------------------------------------------------------
    // ----------------------------------------------------------- TEMAS --------------------------------------------------------------
    //---------------------------------------------------------------------------------------------------------------------------------
    const theme = createTheme({
        palette: {
            mode: user.modeDark,
            background: {
                default:
                    user.modeDark === 'light' ? '#fff' : '#475569',
            },

        },
    })

    const baseBackgroundColor = user.modeDark === 'dark' ? '#475569' : '#fff'

    //---------------------------------------------------------------------------------------------------------------------------------
    // ----------------------------------------------------------- COLUNAS ------------------------------------------------------------
    //---------------------------------------------------------------------------------------------------------------------------------
    const colunas = useMemo<MRT_ColumnDef<ProdutoEstoque>[]>(() => [

        {
            accessorKey: 'descricao', visibleInShowHideMenu: true, header: 'Descrição', size: 400, muiTableBodyCellProps: { align: 'left' }, muiTableHeadCellProps: { align: 'left' },

            Cell: (cell: any) => {

                const rowData = cell.row.original as RowData

                return (<span>{rowData.descricao ? rowData.descricao : <span className={`text-red-400`}>N/D</span>}</span>)
            }
        },

        {
            accessorKey: 'estoque_fisico', visibleInShowHideMenu: true, header: 'Estoque Atual', size: 100, muiTableBodyCellProps: { align: 'left' }, muiTableHeadCellProps: { align: 'left' },

            Cell: (cell: any) => {

                const rowData = cell.row.original as RowData

                return (<span>{rowData.estoque_fisico ? Funcoes.numberFormat(rowData.estoque_fisico, 'decimal') : <span className={`text-red-400`}>N/D</span>}</span>)
            }
        },

        {
            accessorKey: 'preco_c', visibleInShowHideMenu: true, header: 'Preço de Custo (R$)', size: 100, muiTableBodyCellProps: { align: 'right' }, muiTableHeadCellProps: { align: 'right' },

            Cell: (cell: any) => {

                const rowData = cell.row.original as RowData

                return (<span>{rowData.preco_c ? Funcoes.numberFormat(rowData.preco_c, 'currency') : <span className={`text-red-400`}>N/D</span>}</span>)
            }
        },

        {
            accessorKey: 'preco_v', visibleInShowHideMenu: true, header: 'Preço de Venda (R$)', size: 100, muiTableBodyCellProps: { align: 'right' }, muiTableHeadCellProps: { align: 'right' },

            Cell: (cell: any) => {

                const rowData = cell.row.original as RowData

                return (<span>{rowData.preco_v ? Funcoes.numberFormat(rowData.preco_v, 'currency') : <span className={`text-red-400`}>N/D</span>}</span>)
            }
        },

        {
            accessorKey: 'total_custo', visibleInShowHideMenu: true, header: 'Total de Custo ', size: 100, muiTableBodyCellProps: { align: 'right' }, muiTableHeadCellProps: { align: 'right' },

            Cell: (cell: any) => {

                const rowData = cell.row.original as RowData

                return (<span>{rowData.total_custo ? Funcoes.numberFormat(rowData.total_custo, 'currency') : <span className={`text-red-400`}>N/D</span>}</span>)
            }
        },

        {
            accessorKey: 'total_venda', visibleInShowHideMenu: true, header: 'Total de Venda', size: 100, muiTableBodyCellProps: { align: 'right' }, muiTableHeadCellProps: { align: 'right' },

            Cell: (cell: any) => {

                const rowData = cell.row.original as RowData

                return (<span>{rowData.total_venda ? Funcoes.numberFormat(rowData.total_venda, 'currency') : <span className={`text-red-400`}>N/D</span>}</span>)
            }
        },

    ], [])

    const table = useMaterialReactTable({
        columns: colunas,
        data: produtoEstoque,
        autoResetPageIndex: false,
        mrtTheme: (theme) => ({
            baseBackgroundColor: baseBackgroundColor,

        }),
        muiTableHeadCellProps: {
            sx: {
                borderBottom: `0.1px solid ${user.modeDark === 'light' ? bgLineGridColorLight : bgLineGridColorDark}`,
                backgroundColor: corBgGrid,
                color: `#fff`,
                '& .MuiTableSortLabel-root': {
                    color: '#fff !important',
                    '&:hover': {
                        color: '#fff !important',
                    },
                    '&.Mui-active': {
                        color: '#fff !important',
                    },
                    '& .MuiTableSortLabel-icon': {
                        color: '#fff !important',
                    },
                    '&.Mui-active .MuiTableSortLabel-icon': {
                        color: '#fff !important',
                    },
                },
                '& .MuiIconButton-root': {
                    color: '#fff',
                    '&:hover': {
                        color: '#fff',
                    },
                },
            },
        },
        muiTableBodyCellProps: {
            sx: {
                borderBottom: `0.1px solid ${user.modeDark === 'light' ? bgLineGridColorLight : bgLineGridColorDark}`
            }
        },
        enableEditing: false,
        enableTopToolbar: false,
        enableColumnFilters: false,
        enableHiding: false,
        enableFilters: false,
        enableMultiSort: false,
        enableFullScreenToggle: false,
        enableDensityToggle: false,
        localization: MRT_Localization_PT_BR,
        paginationDisplayMode: 'pages',
        renderDetailPanel: ({ row }) => (
            <div className='' style={{ padding: '16px' }} >
                <span className={`text-lg`}>Grade</span>
                <table className={`w-full mt-2`}>
                    <thead className={`h-[30px] bg-blue-800 dark:bg-[#3498DB] text-slate-200`}>
                        <tr>
                            <th className={`w-[30%] text-start px-2 border border-slate-300  dark:border-slate-700`}>Descrição</th>
                            <th className={`w-[10%] text-end px-2 border border-slate-300  dark:border-slate-700`}>Estoque Atual</th>
                            <th className={`w-[15%] text-end px-2 border border-slate-300  dark:border-slate-700`}>Preço de Custo (R$)</th>
                            <th className={`w-[15%] text-end px-2 border border-slate-300  dark:border-slate-700`}>Preço de Venda (R$)</th>
                            <th className={`w-[15%] text-end px-2 border border-slate-300  dark:border-slate-700`}>Total de Custo (R$)</th>
                            <th className={`w-[15%] text-end px-2 border border-slate-300  dark:border-slate-700`}>Total de Venda (R$)</th>

                        </tr>
                    </thead>
                    <tbody className={`bg-slate-50 dark:bg-[#475569] text-black dark:text-slate-200`}>
                        {row.original?.grades.map((subLinha: any, index: any) => (
                            <tr key={index} className={`h-[40px] hover:bg-slate-200 dark:hover:bg-slate-500`}>
                                <td className={`w-[30%] text-start px-2 border border-slate-300 dark:border-slate-700`}>{subLinha.variacao}</td>
                                <td className={`w-[10%] text-end px-2 border border-slate-300 dark:border-slate-700`}>{Funcoes.numberFormat(subLinha.saldoEstoque, 'decimal')}</td>
                                <td className={`w-[15%] text-end px-2 border border-slate-300 dark:border-slate-700`}>{Funcoes.numberFormat(row.original.preco_c, 'decimal')}</td>
                                <td className={`w-[15%] text-end px-2 border border-slate-300 dark:border-slate-700`}>{Funcoes.numberFormat(row.original.preco_v, 'decimal')}</td>
                                <td className={`w-[15%] text-end px-2 border border-slate-300 dark:border-slate-700`}>{Funcoes.numberFormat(row.original.preco_c * subLinha.saldoEstoque, 'decimal')}</td>
                                <td className={`w-[15%] text-end px-2 border border-slate-300 dark:border-slate-700`}>{Funcoes.numberFormat(row.original.preco_v * subLinha.saldoEstoque, 'decimal')}</td>

                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        ),
    })

    return (
        <>
            <ThemeProvider theme={theme} >

                <MaterialReactTable table={table} />

            </ThemeProvider>

        </>
    )
})